
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CampaignFormData } from "@/components/campaigns/modal/CampaignFormSchema";

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (campaignData: CampaignFormData) => {
      console.log('🔥 [CREATE_CAMPAIGN] Starting campaign creation process');
      
      // Usar auth.uid() diretamente para garantir que temos o userId atual
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('🚨 [CREATE_CAMPAIGN] Auth error:', authError);
        throw new Error('Erro de autenticação: ' + authError.message);
      }
      
      if (!user) {
        console.error('🚨 [CREATE_CAMPAIGN] User not authenticated');
        throw new Error('Usuário não autenticado');
      }
      
      const userId = user.id;
      console.log('✅ [CREATE_CAMPAIGN] User authenticated:', userId);
      console.log('🔥 [CREATE_CAMPAIGN] Campaign data:', campaignData);

      // Buscar store_id com query mais específica
      console.log('🔍 [CREATE_CAMPAIGN] Fetching store_id for user:', userId);
      
      const { data: tokenData, error: tokenError } = await supabase
        .from('nuvemshop_tokens')
        .select('store_id, is_active, created_at')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      console.log('🔍 [CREATE_CAMPAIGN] Token query result:', { 
        tokenData, 
        tokenError,
        storeId: tokenData?.store_id
      });

      let storeId = null;

      if (tokenError) {
        if (tokenError.code === 'PGRST116') {
          console.warn('⚠️ [CREATE_CAMPAIGN] No active Nuvemshop tokens found');
          toast.warning('Nenhum token Nuvemshop ativo encontrado. Campanha será criada sem integração.');
        } else {
          console.error('🚨 [CREATE_CAMPAIGN] Error fetching store_id:', tokenError);
          toast.error('Erro ao verificar integração Nuvemshop: ' + tokenError.message);
        }
      } else if (tokenData && tokenData.store_id) {
        // FORÇAR CAST EXPLÍCITO PARA STRING
        storeId = String(tokenData.store_id).toString();
        console.log('✅ [CREATE_CAMPAIGN] Store ID found and converted to string:', storeId);
        console.log('🔍 [CREATE_CAMPAIGN] Store ID type:', typeof storeId);
        console.log('🔍 [CREATE_CAMPAIGN] Store ID length:', storeId.length);
        console.log('🔍 [CREATE_CAMPAIGN] Store ID value check:', storeId === '' ? 'EMPTY' : 'NOT EMPTY');
        
        // VALIDAÇÃO RIGOROSA
        if (!storeId || storeId === 'null' || storeId === 'undefined' || storeId.trim() === '') {
          console.error('🚨 [CREATE_CAMPAIGN] Store ID is invalid after conversion:', storeId);
          storeId = null;
          toast.warning('Store ID inválido. Campanha será criada sem integração.');
        } else {
          toast.success('Integração Nuvemshop detectada - campanha será criada com integração ativa.');
        }
      } else {
        console.warn('⚠️ [CREATE_CAMPAIGN] Token found but no store_id');
        toast.warning('Token Nuvemshop encontrado mas sem store_id. Campanha será criada sem integração.');
      }

      // VALIDAÇÃO FINAL ANTES DO INSERT
      if (storeId) {
        console.log('🔍 [CREATE_CAMPAIGN] Final store_id validation:');
        console.log('  - Value:', storeId);
        console.log('  - Type:', typeof storeId);
        console.log('  - Length:', storeId.length);
        console.log('  - Is string?:', typeof storeId === 'string');
        console.log('  - Is empty?:', storeId === '');
        console.log('  - Is null?:', storeId === null);
        console.log('  - Is undefined?:', storeId === undefined);
      }

      // Criar objeto de dados da campanha
      const campaignInsertData = {
        user_id: userId,
        name: campaignData.name,
        type: campaignData.type,
        product: campaignData.product,
        value: campaignData.value,
        start_date: campaignData.start_date.toISOString().split('T')[0],
        end_date: campaignData.end_date.toISOString().split('T')[0],
        description: campaignData.description,
        max_coupons_per_customer: campaignData.max_coupons_per_customer,
        delay_days: campaignData.delay_days,
        store_id: storeId, // Usando o valor processado
        // Campos de cashback customizado
        use_custom_cashback: campaignData.use_custom_cashback || false,
        custom_cashback_percentage: campaignData.custom_cashback_percentage,
        custom_cashback_multiplier: campaignData.custom_cashback_multiplier,
        custom_cashback_expiration_days: campaignData.custom_cashback_expiration_days,
        status: 'draft'
      };

      console.log('💾 [CREATE_CAMPAIGN] Inserting campaign data:', campaignInsertData);
      console.log('💾 [CREATE_CAMPAIGN] Store ID being inserted (final check):', campaignInsertData.store_id);
      console.log('💾 [CREATE_CAMPAIGN] Store ID type (final check):', typeof campaignInsertData.store_id);

      // TESTE: Inserir com logs ainda mais detalhados
      console.log('🚀 [CREATE_CAMPAIGN] About to execute insert...');
      
      const { data, error } = await supabase
        .from('campaigns')
        .insert(campaignInsertData)
        .select()
        .single();

      console.log('📊 [CREATE_CAMPAIGN] Insert result:', { data, error });

      if (error) {
        console.error('🚨 [CREATE_CAMPAIGN] Error creating campaign:', error);
        console.error('🚨 [CREATE_CAMPAIGN] Error details:', JSON.stringify(error, null, 2));
        throw error;
      }

      console.log('✅ [CREATE_CAMPAIGN] Campaign created successfully:', data);
      console.log('✅ [CREATE_CAMPAIGN] Verifying store_id in created campaign:', data.store_id);
      console.log('✅ [CREATE_CAMPAIGN] Created campaign store_id type:', typeof data.store_id);
      
      // Verificar se o store_id foi salvo corretamente
      if (storeId && data.store_id !== storeId) {
        console.error('🚨 [CREATE_CAMPAIGN] Store ID mismatch after creation:', {
          expected: storeId,
          actual: data.store_id,
          expectedType: typeof storeId,
          actualType: typeof data.store_id
        });
        toast.error('Erro: Store ID não foi salvo corretamente');
      } else if (storeId && data.store_id === storeId) {
        console.log('✅ [CREATE_CAMPAIGN] Store ID correctly saved:', data.store_id);
      } else if (!storeId && !data.store_id) {
        console.log('✅ [CREATE_CAMPAIGN] No store_id expected and none saved (correct behavior)');
      }

      return data;
    },
    onSuccess: (data) => {
      console.log('🎉 [CREATE_CAMPAIGN] Campaign creation success:', data);
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      
      const integrationMessage = data.store_id 
        ? `Integração Nuvemshop ativa (Store: ${data.store_id})` 
        : 'Sem integração Nuvemshop.';
      
      toast.success(`Campanha criada com sucesso! ${integrationMessage}`);
    },
    onError: (error: any) => {
      console.error('💥 [CREATE_CAMPAIGN] Failed to create campaign:', error);
      toast.error("Erro ao criar campanha: " + (error.message || 'Erro desconhecido'));
    },
  });
};
